<template>
  <div class="container p-4 grok-page">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Generación con IA</h3>
      <span class="grok-badge">Capítulo + tipo · revisión antes de importar</span>
    </div>

    <DjangoAuthGate @authenticated="onDjangoAuth" />

    <template v-if="djangoOk">
      <EstudyooLoadingBanner
        :visible="catalogoLoading && !materias.length"
        message="Cargando materias, cursos y tipos de actividad…"
      />
      <EstudyooLoadingBanner
        :visible="grokLoading || grokImportando"
        :message="grokImportando ? 'Importando actividades al catálogo…' : 'Generando contenido con IA…'"
      />
      <div class="grok-panel surface-card border-round p-4 mb-4">
        <div class="flex flex-wrap align-items-center gap-2 mb-3">
          <span
            class="text-xs px-2 py-1 border-round"
            :class="grokConfigurado ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'"
          >
            {{ grokConfigurado ? `IA directa activa (${grokModelo})` : 'IA directa opcional — puedes pegar JSON de otro modelo' }}
          </span>
        </div>

        <p class="text-sm text-600 mb-3">
          Genera actividades sobre los <strong>mismos ejes/capítulos vigentes</strong> que el alumno
          ve en Estudyoo (curso + asignatura). Dos caminos: <strong>copiar el prompt</strong> a otro modelo
          y pegar el JSON aquí, o <strong>generar directo con IA</strong>. Revisa las alternativas antes de importar.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block text-sm mb-1">Curso / nivel</label>
            <Dropdown
              v-model="grokCurso"
              :options="cursoOpts"
              option-label="label"
              placeholder="Elegir curso"
              class="w-full"
              show-clear
              :loading="catalogoLoading"
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Materia</label>
            <Dropdown
              v-model="grokMateria"
              :options="materias"
              option-label="nombre"
              placeholder="Elegir materia"
              class="w-full"
              show-clear
              :loading="catalogoLoading"
              @change="onMateriaChange"
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Eje temático</label>
            <Dropdown
              v-model="grokEje"
              :options="ejeOpts"
              option-label="nombre"
              placeholder="Elegir eje (filtra capítulos)"
              class="w-full"
              show-clear
              :disabled="!grokMateria"
              :loading="ejesLoading"
              @change="onEjeChange"
            />
            <p v-if="grokMateria && !ejesLoading && !ejeOpts.length" class="text-xs text-500 mt-1">
              Sin ejes activos con capítulos para este alcance (mismo criterio que el alumno).
            </p>
          </div>
          <div>
            <label class="block text-sm mb-1">Capítulo <span class="text-red-500">*</span></label>
            <Dropdown
              v-model="grokCapitulo"
              :options="capituloOpts"
              option-label="label"
              placeholder="Elegir capítulo"
              class="w-full"
              show-clear
              filter
              filter-placeholder="Buscar código o nombre…"
              :disabled="!grokMateria"
              :loading="capitulosLoading"
            />
            <p class="text-xs text-500 mt-1">
              {{ capituloOpts.length }} capítulo(s) vigente(s) — mismos que ve el alumno
              <span v-if="grokEje"> · eje «{{ grokEje.nombre }}»</span>
              <span v-if="grokCurso"> · {{ labelCursoRaw(grokCurso) }}</span>
            </p>
            <p v-if="grokMateria && !capitulosLoading && !capituloOpts.length" class="text-xs text-orange-600 mt-1">
              Sin capítulos vigentes para este curso/materia — sincroniza legacy → Django o elige otro nivel.
            </p>
          </div>
          <div>
            <label class="block text-sm mb-1">Tipo de actividad <span class="text-red-500">*</span></label>
            <Dropdown
              v-model="grokTipoSel"
              :options="tipos"
              option-label="nombre"
              placeholder="Elegir tipo"
              class="w-full"
              :loading="catalogoLoading"
            >
              <template #option="{ option }">
                <span>{{ option.nombre }}</span>
                <span class="text-500 text-xs ml-2">({{ option.codigo }})</span>
              </template>
            </Dropdown>
            <p v-if="!catalogoLoading && !tipos.length" class="text-xs text-orange-600 mt-1">
              No hay tipos en Django. Ejecuta el seed de actividades o créalos en el admin de Django.
            </p>
          </div>
          <div>
            <label class="block text-sm mb-1">Cantidad de preguntas <span class="text-red-500">*</span></label>
            <Dropdown
              v-model="grokCantidadPreguntas"
              :options="cantidadPreguntasOpts"
              placeholder="Elegir cantidad"
              class="w-full"
            />
            <p class="text-xs text-500 mt-1">
              Variable: 1 a 20 (no está fijo en 5). Elige cuántas preguntas nuevas generar.
            </p>
          </div>
        </div>

        <div class="mb-1 flex flex-wrap align-items-center justify-content-between gap-2">
          <label class="block text-sm m-0">Guion del video</label>
          <span v-if="pdfFileName" class="text-xs text-600">
            <i class="pi pi-file-pdf mr-1" />{{ pdfFileName }}
            <template v-if="pdfMeta"> · {{ pdfMeta }}</template>
          </span>
        </div>

        <div
          class="guion-drop mb-2"
          :class="{ 'guion-drop--active': pdfDragOver, 'guion-drop--busy': pdfLoading }"
          @dragenter.prevent="onPdfDrag(true)"
          @dragover.prevent="onPdfDrag(true)"
          @dragleave.prevent="onPdfDrag(false)"
          @drop.prevent="onPdfDrop"
        >
          <input
            ref="pdfFileInput"
            type="file"
            accept="application/pdf,.pdf"
            class="hidden"
            @change="onPdfFileInput"
          />
          <div class="flex flex-wrap align-items-center gap-2">
            <ButtonPrime
              label="Adjuntar PDF"
              icon="pi pi-upload"
              class="p-button-sm p-button-outlined"
              :loading="pdfLoading"
              :disabled="pdfLoading"
              @click="pdfFileInput?.click()"
            />
            <ButtonPrime
              v-if="grokGuion || pdfFileName"
              label="Limpiar guion"
              icon="pi pi-times"
              class="p-button-sm p-button-text p-button-secondary"
              :disabled="pdfLoading"
              @click="limpiarGuion"
            />
            <span class="text-xs text-500">
              Arrastra un PDF aquí o adjuntalo; el texto se extrae en el navegador y se pega abajo.
            </span>
          </div>
          <p v-if="pdfLoading" class="text-xs text-primary mt-2 mb-0">
            <i class="pi pi-spin pi-spinner mr-1" />Extrayendo texto del PDF…
          </p>
          <p v-if="pdfError" class="text-xs text-red-600 mt-2 mb-0">{{ pdfError }}</p>
        </div>

        <Textarea
          v-model="grokGuion"
          rows="8"
          class="w-full mb-4"
          placeholder="Pega aquí el guion, o adjunta un PDF arriba para rellenarlo automáticamente…"
        />

        <!-- Paso 1: obtener contenido -->
        <p class="text-sm font-semibold mb-2">1. Obtener JSON (elige una opción)</p>
        <div class="flex flex-wrap gap-2 mb-4">
          <ButtonPrime
            label="Generar prompt (copiar)"
            icon="pi pi-copy"
            class="p-button-outlined"
            :loading="promptLoading"
            @click="generarPrompt"
          />
          <ButtonPrime
            label="Generar con IA"
            icon="pi pi-sparkles"
            :loading="grokLoading"
            :disabled="!grokConfigurado"
            @click="generarGrok"
          />
          <input ref="jsonFileInput" type="file" accept=".json,application/json" class="hidden" @change="onArchivoJson" />
          <ButtonPrime
            label="Adjuntar JSON"
            icon="pi pi-paperclip"
            class="p-button-outlined"
            @click="jsonFileInput?.click()"
          />
        </div>

        <div v-if="grokPrompt" class="mb-4">
          <div class="flex flex-wrap align-items-center gap-2 mb-2">
            <label class="text-sm font-semibold m-0">Prompt para otro modelo</label>
            <ButtonPrime label="Copiar prompt" icon="pi pi-copy" class="p-button-sm p-button-text" @click="copiarPrompt" />
          </div>
          <Textarea v-model="grokPrompt" rows="8" class="w-full text-xs font-mono" readonly />
          <p class="text-xs text-500 mt-1">
            Pega este prompt en ChatGPT, Claude o Grok web. Copia la respuesta JSON y pégala abajo en el editor.
          </p>
        </div>

        <!-- Paso 2: revisar / editar -->
        <p class="text-sm font-semibold mb-2">2. Revisar y editar (español chileno)</p>

        <div v-if="resumenEditor.total" class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <div class="grok-stat">
            <span class="grok-stat__n">{{ resumenEditor.actividades }}</span>
            <span class="grok-stat__l">Actividades</span>
          </div>
          <div class="grok-stat">
            <span class="grok-stat__n">{{ resumenEditor.preguntas }}</span>
            <span class="grok-stat__l">Preguntas</span>
          </div>
          <div class="grok-stat">
            <span class="grok-stat__n">{{ resumenEditor.materiales }}</span>
            <span class="grok-stat__l">Materiales</span>
          </div>
          <div class="grok-stat grok-stat--ok">
            <span class="grok-stat__n">{{ resumenEditor.correctas }}/{{ resumenEditor.preguntas }}</span>
            <span class="grok-stat__l">Con respuesta marcada</span>
          </div>
        </div>

        <TabView v-if="tablasListas" lazy class="grok-tabs mb-3">
          <TabPanel header="Preguntas y alternativas">
            <p class="text-xs text-600 mb-2">
              Revisa enunciados y opciones A–D. La columna resaltada en verde es la respuesta correcta según
              <code>correcta_letra</code>.
            </p>
            <DataTable
              :value="editPreguntas"
              class="p-datatable-sm grok-table"
              scrollable
              scroll-height="420px"
              empty-message="Sin preguntas en el borrador"
            >
              <Column field="orden" header="#" style="width: 3rem">
                <template #body="{ data }">
                  <InputNumber v-model="data.orden" class="w-full" input-class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="enunciado" header="Enunciado" style="min-width: 14rem">
                <template #body="{ data }">
                  <Textarea v-model="data.enunciado" rows="2" class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column header="A" style="min-width: 8rem">
                <template #body="{ data }">
                  <Textarea
                    v-model="data.alt_a"
                    rows="2"
                    class="w-full text-sm grok-alt"
                    :class="{ 'grok-alt--correct': esAltCorrecta(data, 'A') }"
                    @input="syncFromTables"
                  />
                </template>
              </Column>
              <Column header="B" style="min-width: 8rem">
                <template #body="{ data }">
                  <Textarea
                    v-model="data.alt_b"
                    rows="2"
                    class="w-full text-sm grok-alt"
                    :class="{ 'grok-alt--correct': esAltCorrecta(data, 'B') }"
                    @input="syncFromTables"
                  />
                </template>
              </Column>
              <Column header="C" style="min-width: 8rem">
                <template #body="{ data }">
                  <Textarea
                    v-model="data.alt_c"
                    rows="2"
                    class="w-full text-sm grok-alt"
                    :class="{ 'grok-alt--correct': esAltCorrecta(data, 'C') }"
                    @input="syncFromTables"
                  />
                </template>
              </Column>
              <Column header="D" style="min-width: 8rem">
                <template #body="{ data }">
                  <Textarea
                    v-model="data.alt_d"
                    rows="2"
                    class="w-full text-sm grok-alt"
                    :class="{ 'grok-alt--correct': esAltCorrecta(data, 'D') }"
                    @input="syncFromTables"
                  />
                </template>
              </Column>
              <Column field="correcta_letra" header="OK" style="width: 4.5rem">
                <template #body="{ data }">
                  <Dropdown
                    v-model="data.correcta_letra"
                    :options="letrasAlt"
                    placeholder="—"
                    class="w-full"
                    @change="syncFromTables"
                  />
                </template>
              </Column>
              <Column field="explicacion" header="Explicación" style="min-width: 10rem">
                <template #body="{ data }">
                  <Textarea v-model="data.explicacion" rows="2" class="w-full text-xs" @input="syncFromTables" />
                </template>
              </Column>
            </DataTable>
          </TabPanel>

          <TabPanel header="Actividades">
            <DataTable
              :value="editActividades"
              class="p-datatable-sm grok-table"
              scrollable
              scroll-height="320px"
              empty-message="Sin actividades"
            >
              <Column field="titulo" header="Título" style="min-width: 12rem">
                <template #body="{ data }">
                  <InputText v-model="data.titulo" class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="subtitulo" header="Subtítulo" style="min-width: 10rem">
                <template #body="{ data }">
                  <InputText v-model="data.subtitulo" class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="instrucciones" header="Instrucciones" style="min-width: 14rem">
                <template #body="{ data }">
                  <Textarea v-model="data.instrucciones" rows="2" class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="tipo_codigo" header="Tipo" style="width: 9rem">
                <template #body="{ data }">
                  <InputText v-model="data.tipo_codigo" class="w-full text-xs font-mono" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="activa" header="Activa" style="width: 5rem">
                <template #body="{ data }">
                  <Checkbox v-model="data.activa" :binary="true" @change="syncFromTables" />
                </template>
              </Column>
            </DataTable>
          </TabPanel>

          <TabPanel header="Materiales">
            <DataTable
              :value="editMateriales"
              class="p-datatable-sm grok-table"
              scrollable
              scroll-height="320px"
              empty-message="Sin materiales"
            >
              <Column field="titulo" header="Título" style="min-width: 10rem">
                <template #body="{ data }">
                  <InputText v-model="data.titulo" class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="tipo" header="Tipo" style="width: 6rem">
                <template #body="{ data }">
                  <InputText v-model="data.tipo" class="w-full text-xs" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="contenido" header="Contenido" style="min-width: 16rem">
                <template #body="{ data }">
                  <Textarea v-model="data.contenido" rows="3" class="w-full text-sm" @input="syncFromTables" />
                </template>
              </Column>
              <Column field="obligatorio" header="Oblig." style="width: 5rem">
                <template #body="{ data }">
                  <Checkbox v-model="data.obligatorio" :binary="true" @change="syncFromTables" />
                </template>
              </Column>
            </DataTable>
          </TabPanel>

          <TabPanel header="JSON crudo">
            <Textarea
              v-model="grokBorrador"
              rows="16"
              class="w-full font-mono text-xs"
              placeholder='JSON completo…'
              @input="onBorradorEditado"
            />
          </TabPanel>
        </TabView>

        <Textarea
          v-else
          v-model="grokBorrador"
          rows="10"
          class="w-full mb-2 font-mono text-xs"
          placeholder='Pega aquí el JSON de la IA o adjunta un archivo…'
          @input="onBorradorEditado"
        />
        <p v-if="jsonValido === true" class="text-xs text-green-600 mb-2">
          <i class="pi pi-check-circle" /> JSON válido — listo para importar
        </p>
        <p v-else-if="jsonValido === false" class="text-xs text-red-500 mb-2">{{ jsonError }}</p>

        <div class="flex flex-wrap gap-2 mb-3">
          <ButtonPrime label="Validar JSON" icon="pi pi-check" class="p-button-outlined" @click="validarJson" />
          <ButtonPrime
            label="Formatear JSON"
            icon="pi pi-align-left"
            class="p-button-outlined"
            @click="formatearJson"
          />
          <ButtonPrime
            label="Importar al catálogo"
            icon="pi pi-upload"
            class="p-button-success"
            :loading="grokImportando"
            :disabled="!grokBorrador.trim()"
            @click="importarGrokDjango"
          />
          <ButtonPrime
            v-if="grokBorrador"
            label="Descargar JSON"
            icon="pi pi-download"
            class="p-button-outlined"
            @click="descargarBorradorGrok"
          />
        </div>

        <div class="flex align-items-center gap-2 mb-3">
          <input id="grok-importar-auto" v-model="grokImportarAuto" type="checkbox" />
          <label for="grok-importar-auto" class="text-sm cursor-pointer">
            Al generar con IA directa: importar automáticamente sin revisar (no recomendado)
          </label>
        </div>

        <p v-if="grokError" class="text-sm text-red-500">{{ grokError }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ButtonPrime from 'primevue/button';
import Textarea from 'primevue/textarea';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Checkbox from 'primevue/checkbox';
import Dropdown from 'primevue/dropdown';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { ActividadesApi } from '@/services/EstudyooAdminApi';
import { isDjangoAdminAuthenticated, handleDjangoApiError } from '@/services/djangoAdminAuth';
import { compararCursos, labelCurso, labelNivel } from '@/utils/nivelesEscolares';
import { extractTextFromPdfFile } from '@/utils/extractPdfText';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());
const tipos = ref([]);
const materias = ref([]);
const cursos = ref([]);
const ejes = ref([]);
const capitulos = ref([]);
const catalogoLoading = ref(false);
const ejesLoading = ref(false);
const capitulosLoading = ref(false);
const grokGuion = ref('');
const pdfFileInput = ref(null);
const pdfLoading = ref(false);
const pdfDragOver = ref(false);
const pdfFileName = ref('');
const pdfMeta = ref('');
const pdfError = ref('');
const grokCurso = ref(null);
const grokEje = ref(null);
const grokMateria = ref(null);
const grokCapitulo = ref(null);
const grokTipoSel = ref(null);
const grokCantidadPreguntas = ref(5);
const cantidadPreguntasOpts = Array.from({ length: 20 }, (_, i) => i + 1);
const grokPrompt = ref('');
const grokBorrador = ref('');
const grokBorradorObj = ref(null);
const grokError = ref('');
const grokLoading = ref(false);
const promptLoading = ref(false);
const grokImportando = ref(false);
const grokImportarAuto = ref(false);
const grokConfigurado = ref(false);
const grokModelo = ref('grok-2-latest');
const jsonValido = ref(null);
const jsonError = ref('');
const jsonFileInput = ref(null);
const editActividades = ref([]);
const editPreguntas = ref([]);
const editMateriales = ref([]);
const letrasAlt = ['A', 'B', 'C', 'D'];

const tablasListas = computed(
  () => editActividades.value.length > 0
    || editPreguntas.value.length > 0
    || editMateriales.value.length > 0,
);

const resumenEditor = computed(() => {
  const preguntas = editPreguntas.value.length;
  const correctas = editPreguntas.value.filter((p) => {
    const l = String(p.correcta_letra || '').toUpperCase();
    return ['A', 'B', 'C', 'D'].includes(l);
  }).length;
  return {
    actividades: editActividades.value.length,
    preguntas,
    materiales: editMateriales.value.length,
    correctas,
    total: editActividades.value.length + preguntas + editMateriales.value.length,
  };
});

function normalizarBorrador(obj) {
  const data = obj?.borrador && typeof obj.borrador === 'object' ? obj.borrador : obj;
  if (!data || typeof data !== 'object') {
    return { actividades: [], preguntas: [], materiales: [] };
  }
  return {
    actividades: (data.actividades || []).map((a) => ({
      ...a,
      activa: a.activa !== false && String(a.activa).toLowerCase() !== 'false',
    })),
    preguntas: (data.preguntas || []).map((p) => ({ ...p })),
    materiales: (data.materiales || []).map((m) => ({
      ...m,
      obligatorio: m.obligatorio !== false && String(m.obligatorio).toLowerCase() !== 'false',
    })),
  };
}

function aplicarBorradorATablas(obj) {
  const n = normalizarBorrador(obj);
  editActividades.value = n.actividades;
  editPreguntas.value = n.preguntas;
  editMateriales.value = n.materiales;
  grokBorradorObj.value = {
    actividades: n.actividades,
    preguntas: n.preguntas,
    materiales: n.materiales,
  };
}

function syncFromTables() {
  if (!tablasListas.value) return;
  grokBorradorObj.value = {
    actividades: editActividades.value,
    preguntas: editPreguntas.value,
    materiales: editMateriales.value,
  };
  grokBorrador.value = JSON.stringify(grokBorradorObj.value, null, 2);
  jsonValido.value = true;
  jsonError.value = '';
}

function esAltCorrecta(preg, letra) {
  return String(preg?.correcta_letra || '').toUpperCase() === letra;
}

function limpiarTablas() {
  editActividades.value = [];
  editPreguntas.value = [];
  editMateriales.value = [];
}

function labelCursoRaw(c) {
  return labelCurso(c);
}

const cursoOpts = computed(() =>
  [...cursos.value]
    .sort(compararCursos)
    .map((c) => ({ ...c, label: labelCursoRaw(c) })),
);

function capituloLabel(c) {
  const niv = c.nivel_simce ? labelNivel(c.nivel_simce) : '';
  return `${c.codigo || '—'} · ${c.nombre}${niv ? ` (${niv})` : ''}`;
}

const capituloOpts = computed(() => {
  let list = capitulos.value || [];
  if (grokEje.value?.id) {
    const eid = String(grokEje.value.id);
    list = list.filter((c) => String(c.eje || c.eje_id || '') === eid);
  }
  return list.map((c) => ({ ...c, label: capituloLabel(c) }));
});

/** Ejes que tienen al menos un capítulo vigente en el alcance actual (como el alumno). */
const ejeOpts = computed(() => {
  const list = ejes.value || [];
  const caps = capitulos.value || [];
  if (!caps.length) return list;
  const ids = new Set(
    caps.map((c) => String(c.eje || c.eje_id || '')).filter(Boolean),
  );
  if (!ids.size) return list;
  return list.filter((e) => ids.has(String(e.id)));
});

watch(grokCurso, async () => {
  grokEje.value = null;
  grokCapitulo.value = null;
  if (grokMateria.value?.id) {
    await Promise.all([cargarEjes(), cargarCapitulos()]);
  }
});

function onDjangoAuth(ok) {
  djangoOk.value = !!ok;
  if (ok) {
    cargarCatalogo();
    cargarGrokStatus();
  }
}

function onPdfDrag(active) {
  pdfDragOver.value = !!active;
}

function limpiarGuion() {
  grokGuion.value = '';
  pdfFileName.value = '';
  pdfMeta.value = '';
  pdfError.value = '';
  if (pdfFileInput.value) pdfFileInput.value.value = '';
}

async function onPdfFileInput(ev) {
  const file = ev.target?.files?.[0];
  if (pdfFileInput.value) pdfFileInput.value.value = '';
  if (file) await procesarPdfFile(file);
}

async function onPdfDrop(ev) {
  pdfDragOver.value = false;
  const file = ev.dataTransfer?.files?.[0];
  if (file) await procesarPdfFile(file);
}

async function procesarPdfFile(file) {
  pdfLoading.value = true;
  pdfError.value = '';
  pdfMeta.value = '';
  try {
    const { text, pages, truncated, fileName } = await extractTextFromPdfFile(file);
    grokGuion.value = text;
    pdfFileName.value = fileName;
    pdfMeta.value = truncated
      ? `${pages} pág. (texto truncado)`
      : `${pages} pág. · ${text.length.toLocaleString('es-CL')} caracteres`;
    toast.add({
      severity: 'success',
      summary: 'PDF leído',
      detail: truncated
        ? 'Se extrajo el texto (truncado por tamaño). Revísalo antes de generar.'
        : 'Guion rellenado desde el PDF. Puedes editarlo antes de generar el prompt.',
      life: 5000,
    });
  } catch (e) {
    pdfError.value = e.message || 'No se pudo leer el PDF.';
    toast.add({
      severity: 'error',
      summary: 'PDF',
      detail: pdfError.value,
      life: 7000,
    });
  } finally {
    pdfLoading.value = false;
  }
}

function cantidadPreguntasNormalizada() {
  const n = Number(grokCantidadPreguntas.value);
  if (!Number.isFinite(n)) return 5;
  return Math.min(20, Math.max(1, Math.round(n)));
}

function payloadFormulario() {
  return {
    guion: grokGuion.value,
    curso: grokCurso.value ? labelCursoRaw(grokCurso.value) : '',
    materia: grokMateria.value?.nombre || '',
    capitulo: grokCapitulo.value?.nombre || '',
    capitulo_codigo: grokCapitulo.value?.codigo || '',
    eje: grokEje.value?.nombre || '',
    tipo_codigo: grokTipoSel.value?.codigo || '',
    cantidad_preguntas: cantidadPreguntasNormalizada(),
  };
}

function validarCamposBase() {
  if (!grokCapitulo.value?.codigo) {
    grokError.value = 'Selecciona un capítulo.';
    return false;
  }
  if (!grokTipoSel.value?.codigo) {
    grokError.value = 'Selecciona un tipo de actividad.';
    return false;
  }
  const nPreg = Number(grokCantidadPreguntas.value);
  if (!Number.isFinite(nPreg) || nPreg < 1 || nPreg > 20) {
    grokError.value = 'Indica una cantidad de preguntas entre 1 y 20.';
    return false;
  }
  if ((grokGuion.value || '').trim().length < 40) {
    grokError.value = 'El guion debe tener al menos 40 caracteres.';
    return false;
  }
  grokError.value = '';
  return true;
}

function onBorradorEditado() {
  jsonValido.value = null;
  jsonError.value = '';
  grokBorradorObj.value = null;
  limpiarTablas();
}

function onArchivoJson(ev) {
  const file = ev.target?.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      grokBorrador.value = String(reader.result || '');
      if (validarJson()) {
        toast.add({ severity: 'success', summary: 'JSON adjunto', detail: file.name, life: 4000 });
      }
    } catch (e) {
      grokError.value = e.message || 'No se pudo leer el archivo.';
    } finally {
      ev.target.value = '';
    }
  };
  reader.onerror = () => {
    toast.add({ severity: 'error', summary: 'Archivo', detail: 'No se pudo leer el JSON.', life: 5000 });
    ev.target.value = '';
  };
  reader.readAsText(file, 'UTF-8');
}

function validarJson() {
  if (!grokBorrador.value.trim()) {
    jsonValido.value = false;
    jsonError.value = 'El editor está vacío.';
    return false;
  }
  try {
    const parsed = JSON.parse(grokBorrador.value);
    grokBorradorObj.value = parsed;
    aplicarBorradorATablas(parsed);
    jsonValido.value = true;
    jsonError.value = '';
    toast.add({
      severity: 'success',
      summary: 'JSON',
      detail: `${resumenEditor.value.preguntas} pregunta(s), ${resumenEditor.value.actividades} actividad(es).`,
      life: 4000,
    });
    return true;
  } catch (e) {
    jsonValido.value = false;
    jsonError.value = `JSON inválido: ${e.message}`;
    grokBorradorObj.value = null;
    return false;
  }
}

function formatearJson() {
  if (!validarJson()) return;
  syncFromTables();
  grokBorrador.value = JSON.stringify(grokBorradorObj.value, null, 2);
}

async function cargarCatalogo() {
  catalogoLoading.value = true;
  try {
    [tipos.value, materias.value, cursos.value] = await Promise.all([
      ActividadesApi.listTipos(),
      ActividadesApi.listMaterias(),
      ActividadesApi.listCursos(),
    ]);
    if (!tipos.value?.length) {
      toast.add({
        severity: 'warn',
        summary: 'Tipos de actividad',
        detail: 'No hay tipos en Django. Usa seed_actividades o el admin Django.',
        life: 8000,
      });
    } else if (!grokTipoSel.value) {
      grokTipoSel.value =
        tipos.value.find((t) => t.codigo === 'image_alternatives') || tipos.value[0];
    }
  } catch (e) {
    if (!handleDjangoApiError(e, { toast })) {
      toast.add({ severity: 'error', summary: 'Catálogo', detail: e.message, life: 5000 });
    }
  } finally {
    catalogoLoading.value = false;
  }
}

async function cargarEjes() {
  if (!grokMateria.value?.id) {
    ejes.value = [];
    return;
  }
  ejesLoading.value = true;
  try {
    // Solo activos (mismo universo vigente que el alumno tras sync).
    ejes.value = await ActividadesApi.listEjes({ materia_id: grokMateria.value.id });
  } catch (e) {
    ejes.value = [];
    if (!handleDjangoApiError(e, { toast })) {
      toast.add({ severity: 'error', summary: 'Ejes', detail: e.message, life: 5000 });
    }
  } finally {
    ejesLoading.value = false;
  }
}

function paramsCapitulosAlumno() {
  const params = { materia_id: grokMateria.value.id };
  if (grokCurso.value?.id) params.curso_id = grokCurso.value.id;
  if (grokCurso.value?.nivel_simce) params.nivel_simce = grokCurso.value.nivel_simce;
  return params;
}

async function cargarCapitulos() {
  if (!grokMateria.value?.id) {
    capitulos.value = [];
    return;
  }
  capitulosLoading.value = true;
  try {
    // Alcance alineado al alumno: materia + curso/nivel + solo activos.
    // El filtro por eje se aplica en cliente (capituloOpts) para no vaciar ejeOpts.
    capitulos.value = await ActividadesApi.listCapitulos(paramsCapitulosAlumno());
  } catch (e) {
    capitulos.value = [];
    if (!handleDjangoApiError(e, { toast })) {
      toast.add({ severity: 'error', summary: 'Capítulos', detail: e.message, life: 5000 });
    }
  } finally {
    capitulosLoading.value = false;
  }
}

async function onMateriaChange() {
  grokEje.value = null;
  grokCapitulo.value = null;
  await Promise.all([cargarEjes(), cargarCapitulos()]);
}

function onEjeChange() {
  grokCapitulo.value = null;
}

async function cargarGrokStatus() {
  try {
    const s = await ActividadesApi.grokStatus();
    grokConfigurado.value = !!s.configurado;
    grokModelo.value = s.modelo || 'grok-2-latest';
  } catch {
    grokConfigurado.value = false;
  }
}

function toastImportacion(r, prefix = 'Importación') {
  const a = r.actividades || {};
  const p = r.preguntas || {};
  const m = r.materiales || {};
  toast.add({
    severity: r.ok ? 'success' : 'warn',
    summary: prefix,
    detail: `Actividades: +${a.creadas || 0}/~${a.actualizadas || 0} · Preguntas: +${p.creadas || 0} · Materiales: +${m.creadas || 0}${r.errores?.length ? ` · ${r.errores.length} error(es)` : ''}`,
    life: 8000,
  });
}

async function generarPrompt() {
  if (!validarCamposBase()) return;
  promptLoading.value = true;
  try {
    const r = await ActividadesApi.obtenerPromptActividad(payloadFormulario());
    grokPrompt.value = r.prompt || '';
    toast.add({
      severity: 'success',
      summary: 'Prompt listo',
      detail: 'Copia el prompt y pégalo en tu modelo favorito. Luego pega el JSON aquí abajo.',
      life: 6000,
    });
  } catch (e) {
    grokError.value = e.response?.data?.detail || e.message || 'Error al generar prompt.';
  } finally {
    promptLoading.value = false;
  }
}

async function copiarPrompt() {
  if (!grokPrompt.value) return;
  try {
    await navigator.clipboard.writeText(grokPrompt.value);
    toast.add({ severity: 'info', summary: 'Copiado', detail: 'Prompt en el portapapeles.', life: 3000 });
  } catch {
    toast.add({ severity: 'warn', summary: 'Copia manual', detail: 'Selecciona el texto del prompt y copia con Ctrl+C.', life: 5000 });
  }
}

async function importarGrokDjango() {
  if (tablasListas.value) syncFromTables();
  if (!validarJson()) {
    toast.add({ severity: 'warn', summary: 'Revisa el JSON', detail: jsonError.value || 'Corrige el formato antes de importar.', life: 5000 });
    return;
  }
  grokImportando.value = true;
  grokError.value = '';
  try {
    const metaNivel =
      grokCapitulo.value?.codigo && grokCurso.value?.nivel_simce
        ? {
            capitulo_codigo: grokCapitulo.value.codigo,
            nivel_simce: grokCurso.value.nivel_simce,
          }
        : null;
    const r = await ActividadesApi.importarGrokJson(grokBorradorObj.value, null, metaNivel);
    toastImportacion(r, 'Importación');
    if (r.errores?.length) {
      grokError.value = r.errores.map((e) => e.error || JSON.stringify(e)).join(' · ');
    } else if (r.ok) {
      toast.add({
        severity: 'info',
        summary: 'Siguiente paso',
        detail: 'Asigna la actividad al curso en el Mantenedor de actividades.',
        life: 6000,
      });
    }
  } catch (e) {
    grokError.value = e.response?.data?.detail || e.message || 'Error al importar.';
  } finally {
    grokImportando.value = false;
  }
}

async function generarGrok() {
  if (!validarCamposBase()) return;
  grokBorrador.value = '';
  grokBorradorObj.value = null;
  limpiarTablas();
  jsonValido.value = null;
  grokLoading.value = true;
  try {
    const r = await ActividadesApi.generarConGrok({
      ...payloadFormulario(),
      importar: grokImportarAuto.value,
    });
    if (r.prompt) {
      grokPrompt.value = r.prompt;
    }
    if (!r.ok) {
      grokError.value = r.detail || 'No se pudo generar con Grok.';
      if (r.prompt && !grokPrompt.value) {
        grokPrompt.value = r.prompt;
      }
      return;
    }
    grokBorradorObj.value = r.borrador;
    aplicarBorradorATablas(r.borrador);
    grokBorrador.value = JSON.stringify(grokBorradorObj.value, null, 2);
    jsonValido.value = true;
    if (r.importacion) {
      toastImportacion(r.importacion, 'Importación automática');
      if (!r.importacion.ok && r.importacion.errores?.length) {
        grokError.value = r.importacion.errores.map((e) => e.error || JSON.stringify(e)).join(' · ');
      }
    } else {
      const pedidas = cantidadPreguntasNormalizada();
      const obtenidas = Array.isArray(r.borrador?.preguntas)
        ? r.borrador.preguntas.length
        : (r.preguntas_generadas || resumenEditor.value.preguntas);
      const aviso = r.aviso || (
        obtenidas < pedidas
          ? `Se pidieron ${pedidas} y llegaron ${obtenidas}. Revisa o vuelve a generar.`
          : null
      );
      toast.add({
        severity: aviso ? 'warn' : 'success',
        summary: 'IA',
        detail: aviso
          || `Listo: ${obtenidas} pregunta(s) (pedidas: ${pedidas}). Revisa e importa al catálogo.`,
        life: 8000,
      });
    }
  } catch (e) {
    grokError.value = e.response?.data?.detail || e.message || 'Error al generar.';
  } finally {
    grokLoading.value = false;
  }
}

function descargarBorradorGrok() {
  if (!grokBorrador.value) return;
  const blob = new Blob([grokBorrador.value], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `borrador-actividad-${grokCapitulo.value?.codigo || 'estudyoo'}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

onMounted(() => {
  if (djangoOk.value) {
    cargarCatalogo();
    cargarGrokStatus();
  }
});
</script>

<style scoped>
.grok-page {
  max-width: 960px;
}
.grok-badge {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ede9fe 0%, #fce7f3 100%);
  color: #5b21b6;
  border: 1px solid #c4b5fd;
}
.grok-panel {
  border: 1px solid #c4b5fd;
  background: linear-gradient(180deg, rgba(237, 233, 254, 0.35) 0%, transparent 120px);
}
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
.hidden {
  display: none;
}
.grok-stat {
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.grok-stat__n {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}
.grok-stat__l {
  font-size: 0.72rem;
  color: #64748b;
}
.grok-stat--ok .grok-stat__n {
  color: #059669;
}
.grok-tabs :deep(.p-tabview-panels) {
  padding: 0.75rem 0 0;
}
.grok-table :deep(.p-datatable-tbody > tr > td) {
  vertical-align: top;
  padding: 0.35rem;
}
.grok-alt--correct {
  background: #ecfdf5 !important;
  border: 1px solid #6ee7b7 !important;
  border-radius: 6px;
}
.guion-drop {
  border: 1.5px dashed #cbd5e1;
  border-radius: 10px;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.guion-drop--active {
  border-color: #6366f1;
  background: #eef2ff;
}
.guion-drop--busy {
  opacity: 0.85;
}
</style>
